const GAME_CONFIG = {
  laneX: { left: -2.2, right: 2.2 },
  hitZ: 9,
  spawnZ: -60,
  missZ: 12,
  noteSpeed: 40,
  bpm: 125, // Count-in tempo; recorded attacks follow songMap.json.
  rhythm: {
    countInBeats: 4,
    songLeadSeconds: 3, // Play the recorded intro during the countdown.
    startLeadSeconds: 0.12,
    perfectWindow: 0.04,
    greatWindow: 0.08,
    toleranceScale: 1.6,
    comboMilestones: [8, 24],
    feedbackSeconds: 0.65,
    guideVolume: 0.035,
    hitVolume: 0.13,
    clockCorrectionRate: 0.025,
    cameraMotionScale: 0.3,
  },
  colors: {
    left: 0x7EC54A,
    right: 0xF7E04B,
    bg: 0x0A1A0F,
    grid: 0x2E4A2B,
    road: 0x2b2e36,
    roadDetail: 0x1d1f25,
    roadShoulder: 0x121318,
    roadLine: 0xF7E04B,
    roadEdgeLine: 0xC5E6A0,
    pothole: 0x07080b,
    arcadePlaster: 0xe7c19f,
    arcadeTrim: 0xa54c2a,
    corridorShadow: 0x6d523f,
    shopPlaster: 0xf0d4b8,
    shopTrim: 0x8b4728,
    shopWood: 0x4c2f26,
    shopGlass: 0x6e8792,
    shopAwning: 0x2E7D32,
    stone: 0xb7aa97,
    danger: 0xff0044,
    hitFeedback: 0x2E7D32,
    slash: 0xF7E04B,
  },
  ground: {
    roadWidth: 16,
    shoulderWidth: 24,
    length: 180,
    textureRepeatY: 3.75,
    scrollFactor: 0.55,
    decals: {
      yOffset: 0.02,
      laneMargin: 1.4,
      scaleJitter: 0.3,
      recycleZ: 18,
      types: {
        pothole: { count: 6, size: 2.2, spin: true },
        puddle: { count: 5, size: 2.6, spin: true, frameColumns: 4, frameRows: 1, frameDuration: 0.3 },
        rubble: { count: 4, size: 1.8, spin: true },
        drain: { count: 3, size: 1.6, spin: false },
      },
    },
  },
  corridor: {
    moduleLength: 16,
    leftWallX: -12.05,
    rightWallX: 12.05,
    groundOverlap: 0.42,
  },
  park: {
    water: {
      minX: -65,
      maxX: -8.2,
      recycleZ: 18,
      opacity: 0.78,
    },
    terrestrialCount: 3,
    aquaticCount: { ducks: 2, axolotls: 4 },
    aerialCount: 1,
    swimFps: 8,
  },
  timeMilestones: [0, 20, 45],
  time: {
    maxDuration: 180,
    maxBuffer: 60,
    initial: 30,
  },
  runner: {
    staticZ: 4.2,
  },
  notes: {
    hold: {
      bonusPerSecond: 2.5,
      releaseGraceSeconds: 0.12,
    },
  },
  ambientAnimals: {
    count: 2,
    recycleZ: 15,
    dogHeight: 2.4,
    cyclistHeight: 4.2,
    catHeight: 3.53,
    lateralMin: 5.6,
    lateralMax: 7.8,
    speedMin: 0.55,
    speedMax: 0.9,
    // El ciclista de frente debe superar el scroll del mundo (1.0) para que
    // realmente se acerque a la cámara al mismo ritmo relativo con el que el
    // de espaldas se aleja (1 + speedMin..speedMax).
    cyclistSpeedMin: 1.5,
    cyclistSpeedMax: 1.9,
    bobAmp: 0.21,
    wanderAmpMin: 0.45,
    wanderAmpMax: 0.9,
  },
  score: {
    basePerSecond: 0,
    bonusPerHit: 1,
    bonusMaxStreak: 1,
  },
};

const CAMERA_STREAK_TIERS = [
  { id: 'base', minStreak: 0 },
  { id: 'pulse', minStreak: 10 },
  { id: 'sway', minStreak: 25 },
  { id: 'wobble', minStreak: 50 },
];

const GAME_STAGES = [
  {
    id: 'intro',
    startTime: 0,
    endTime: 10,
    spawn: {
      seed: 101,
      lanePatterns: [['left'], ['right'], ['left', 'right']],
      rules: [{ interval: 8, offset: 0, chance: 1 }],
    },
    audio: {
      enableAccent: false,
      enableShimmer: false,
      enableSlashLayer: true,
      enableArpLayer: true,
      enableLeadLayer: true,
    },
    slash: {
      seed: 301,
      variantCount: 24,
      width: 16,
      height: 0.8,
      xRange: 3.5,
      yRange: 2.25,
      rotationRange: Math.PI * 2,
      scaleXStep: 0.3,
      scaleYStart: 1,
      scaleYDecay: 0.1,
      opacityDecay: 0.08,
      frameMs: 16,
    },
    camera: {
      baseX: 0,
      baseY: 3.8,
      baseZ: 15,
      baseFov: 70,
      tiers: {
        base: { beatPulseDepth: 0, swayAmpX: 0, swayFreq: 1.5, rotZAmp: 0, bobAmpY: 0, bobFreq: 3 },
        pulse: { beatPulseDepth: 2, swayAmpX: 0, swayFreq: 1.5, rotZAmp: 0, bobAmpY: 0, bobFreq: 3 },
        sway: { beatPulseDepth: 2, swayAmpX: 0.75, swayFreq: 1.3, rotZAmp: 0.025, bobAmpY: 0, bobFreq: 3 },
        wobble: { beatPulseDepth: 4, swayAmpX: 1.1, swayFreq: 1.6, rotZAmp: 0.04, bobAmpY: 0.75, bobFreq: 2.6 },
      },
    },
  },
  {
    id: 'drive',
    startTime: 10,
    endTime: 60,
    spawn: {
      seed: 202,
      lanePatterns: [['left'], ['right'], ['left', 'right'], ['left']],
      rules: [{ interval: 4, offset: 0, chance: 1 }],
      hold: { chance: 0.14, ticks: [4, 6], gapTicks: 4 },
    },
    audio: {
      enableAccent: false,
      enableShimmer: false,
      enableSlashLayer: true,
      enableArpLayer: true,
      enableLeadLayer: true,
    },
    slash: {
      seed: 302,
      variantCount: 32,
      width: 17,
      height: 0.85,
      xRange: 4.25,
      yRange: 2.75,
      rotationRange: Math.PI * 2,
      scaleXStep: 0.32,
      scaleYStart: 1.05,
      scaleYDecay: 0.1,
      opacityDecay: 0.082,
      frameMs: 16,
    },
    camera: {
      baseX: 0,
      baseY: 3.8,
      baseZ: 15,
      baseFov: 70,
      tiers: {
        base: { beatPulseDepth: 0, swayAmpX: 0, swayFreq: 1.5, rotZAmp: 0, bobAmpY: 0, bobFreq: 3 },
        pulse: { beatPulseDepth: 3, swayAmpX: 0, swayFreq: 1.5, rotZAmp: 0, bobAmpY: 0, bobFreq: 3 },
        sway: { beatPulseDepth: 3, swayAmpX: 1.5, swayFreq: 1.5, rotZAmp: 0.05, bobAmpY: 0, bobFreq: 3 },
        wobble: { beatPulseDepth: 5, swayAmpX: 1.7, swayFreq: 1.9, rotZAmp: 0.055, bobAmpY: 0.9, bobFreq: 2.8 },
      },
    },
  },
  {
    id: 'rush',
    startTime: 60,
    endTime: 120,
    spawn: {
      seed: 303,
      lanePatterns: [['left'], ['right'], ['left', 'right'], ['right'], ['left', 'right']],
      rules: [
        { interval: 4, offset: 0, chance: 1 },
        { interval: 4, offset: 2, chance: 0.3 },
      ],
      hold: { chance: 0.2, ticks: [4, 8, 12], gapTicks: 4 },
    },
    audio: {
      enableAccent: true,
      enableShimmer: false,
      enableSlashLayer: true,
      enableArpLayer: true,
      enableLeadLayer: true,
    },
    slash: {
      seed: 3031,
      variantCount: 40,
      width: 18,
      height: 0.9,
      xRange: 4.75,
      yRange: 3,
      rotationRange: Math.PI * 2,
      scaleXStep: 0.34,
      scaleYStart: 1.1,
      scaleYDecay: 0.11,
      opacityDecay: 0.084,
      frameMs: 16,
    },
    camera: {
      baseX: 0,
      baseY: 3.9,
      baseZ: 14.75,
      baseFov: 69,
      tiers: {
        base: { beatPulseDepth: 1, swayAmpX: 0.3, swayFreq: 1.2, rotZAmp: 0.01, bobAmpY: 0, bobFreq: 3 },
        pulse: { beatPulseDepth: 3.5, swayAmpX: 0.5, swayFreq: 1.4, rotZAmp: 0.02, bobAmpY: 0, bobFreq: 3 },
        sway: { beatPulseDepth: 4, swayAmpX: 1.8, swayFreq: 1.7, rotZAmp: 0.06, bobAmpY: 0.25, bobFreq: 3.4 },
        wobble: { beatPulseDepth: 5.5, swayAmpX: 2.1, swayFreq: 2.2, rotZAmp: 0.07, bobAmpY: 1.2, bobFreq: 3.2 },
      },
    },
  },
  {
    id: 'climax',
    startTime: 120,
    endTime: GAME_CONFIG.time.maxDuration,
    spawn: {
      seed: 404,
      lanePatterns: [['left'], ['right'], ['left', 'right'], ['left', 'right'], ['right']],
      rules: [
        { interval: 4, offset: 0, chance: 1 },
        { interval: 4, offset: 2, chance: 0.4 },
        { interval: 2, offset: 1, chance: 0.08 },
      ],
      hold: { chance: 0.26, ticks: [4, 8, 12, 16], gapTicks: 4 },
    },
    audio: {
      enableAccent: true,
      enableShimmer: true,
      enableSlashLayer: true,
      enableArpLayer: true,
      enableLeadLayer: true,
    },
    slash: {
      seed: 4041,
      variantCount: 56,
      width: 19,
      height: 0.95,
      xRange: 5.25,
      yRange: 3.25,
      rotationRange: Math.PI * 2,
      scaleXStep: 0.36,
      scaleYStart: 1.15,
      scaleYDecay: 0.12,
      opacityDecay: 0.086,
      frameMs: 16,
    },
    camera: {
      baseX: 0,
      baseY: 4,
      baseZ: 14.5,
      baseFov: 68,
      tiers: {
        base: { beatPulseDepth: 1.5, swayAmpX: 0.45, swayFreq: 1.4, rotZAmp: 0.015, bobAmpY: 0.15, bobFreq: 3.2 },
        pulse: { beatPulseDepth: 4, swayAmpX: 0.8, swayFreq: 1.7, rotZAmp: 0.03, bobAmpY: 0.4, bobFreq: 3.5 },
        sway: { beatPulseDepth: 5, swayAmpX: 2.2, swayFreq: 2.1, rotZAmp: 0.07, bobAmpY: 1, bobFreq: 3.6 },
        wobble: { beatPulseDepth: 6.5, swayAmpX: 2.6, swayFreq: 2.6, rotZAmp: 0.08, bobAmpY: 1.5, bobFreq: 4 },
      },
    },
  },
];

const getStageById = (stageId) => GAME_STAGES.find((stage) => stage.id === stageId) ?? GAME_STAGES[0];

const getStageForElapsedTime = (elapsedTime) => {
  const safeTime = Math.max(0, elapsedTime);
  return GAME_STAGES.find((stage) => safeTime >= stage.startTime && safeTime < stage.endTime) ?? GAME_STAGES[GAME_STAGES.length - 1];
};

const getCameraTierForStreak = (streakTime) => {
  let activeTier = CAMERA_STREAK_TIERS[0];
  CAMERA_STREAK_TIERS.forEach((tier) => {
    if (streakTime >= tier.minStreak) activeTier = tier;
  });
  return activeTier;
};

const calculateTimeMultiplier = (streakTime) => {
  if (streakTime >= GAME_CONFIG.timeMilestones[2]) return 3;
  if (streakTime >= GAME_CONFIG.timeMilestones[1]) return 2;
  return 1;
};

const getPenaltyForTime = (elapsedTime) => {
  if (elapsedTime < 30) return 3.0;
  if (elapsedTime < 60) return 8.0;
  if (elapsedTime < 120) return 15.0;
  return 25.0;
};

const getHitsUntilLose = (elapsedTime, timeLeft) => {
  const safeTimeLeft = Math.max(0, timeLeft);
  const penalty = getPenaltyForTime(elapsedTime);
  if (penalty <= 0) return 0;
  return safeTimeLeft / penalty;
};

const getDangerDistanceRatio = (elapsedTime, timeLeft) => {
  const penalty = getPenaltyForTime(elapsedTime);
  if (penalty <= 0) return 0;

  const hitsUntilLose = getHitsUntilLose(elapsedTime, timeLeft);
  const maxHitsForStage = Math.max(1, GAME_CONFIG.time.maxBuffer / penalty);
  return Math.min(hitsUntilLose / maxHitsForStage, 1);
};

module.exports = { GAME_CONFIG, CAMERA_STREAK_TIERS, GAME_STAGES, getStageById, getStageForElapsedTime, getCameraTierForStreak, calculateTimeMultiplier, getPenaltyForTime, getHitsUntilLose, getDangerDistanceRatio };
